import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CreditCard, 
  Truck, 
  User, 
  Lock,
  CheckCircle
} from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import StyledButton from '../components/ui/StyledButton';
import PaypalButton from '../components/payments/PaypalButton';
import StripeCardForm from '../components/payments/StripeCardForm';
import StripeProvider from '../components/payments/StripeProvider';

const CheckoutPage = () => {
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    postalCode: user?.postal_code || '',
    country: user?.country || 'France'
  });
  
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [currentStep, setCurrentStep] = useState(3);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePaymentSuccess = async (paymentData) => {
    try {
      // Rediriger vers la page de confirmation
      navigate('/order-confirmation', { 
        state: { 
          orderId: paymentData.orderId,
          orderNumber: paymentData.orderNumber,
          total: paymentData.amount,
          paymentMethod: paymentData.paymentMethod
        }
      });
      
      // Vider le panier
      await clearCart();
    } catch (error) {
      console.error('Error processing payment success:', error);
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    // Error is handled by the payment component
  };

  const steps = [
    { id: 1, name: 'Informations', icon: User },
    { id: 2, name: 'Livraison', icon: Truck },
    { id: 3, name: 'Paiement', icon: CreditCard }
  ];

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">
            Votre panier est vide
          </h1>
          <p className="text-gray-300 mb-6">
            Ajoutez des produits à votre panier pour continuer
          </p>
          <StyledButton
            onClick={() => navigate('/products')}
            variant="primary"
            size="lg"
          >
            Voir nos produits
          </StyledButton>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            Finaliser votre commande
          </h1>
          <p className="text-gray-300">
            Complétez vos informations pour finaliser votre achat
          </p>
        </div>
        
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-8">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                  currentStep >= step.id 
                    ? 'border-iranian-500 bg-iranian-500 text-white' 
                    : 'border-gray-600 text-gray-400'
                }`}>
                  <step.icon className="h-6 w-6" />
                </div>
                <span className={`ml-3 text-sm font-medium ${
                  currentStep >= step.id ? 'text-white' : 'text-gray-400'
                }`}>
                  {step.name}
                </span>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-0.5 ml-8 ${
                    currentStep > step.id ? 'bg-iranian-500' : 'bg-gray-600'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <div className="space-y-8">
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-gray-800 rounded-xl p-6"
                >
                  <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                    <User className="h-5 w-5 mr-2" />
                    Informations personnelles
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-iranian-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Nom *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-iranian-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-iranian-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-iranian-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <StyledButton
                      onClick={() => setCurrentStep(2)}
                      variant="primary"
                      size="lg"
                      fullWidth
                    >
                      Continuer
                    </StyledButton>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Shipping Information */}
              {currentStep === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-gray-800 rounded-xl p-6"
                >
                  <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                    <Truck className="h-5 w-5 mr-2" />
                    Adresse de livraison
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Adresse *
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-iranian-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Ville *
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-iranian-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Code postal *
                        </label>
                        <input
                          type="text"
                          name="postalCode"
                          value={formData.postalCode}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-iranian-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Pays *
                        </label>
                        <select
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-iranian-500 focus:border-transparent"
                        >
                          <option value="France">France</option>
                          <option value="Belgique">Belgique</option>
                          <option value="Suisse">Suisse</option>
                          <option value="Canada">Canada</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex space-x-4">
                    <StyledButton
                      onClick={() => setCurrentStep(1)}
                      variant="secondary"
                      size="lg"
                    >
                      Retour
                    </StyledButton>
                    <StyledButton
                      onClick={() => setCurrentStep(3)}
                      variant="primary"
                      size="lg"
                      className="flex-1"
                    >
                      Continuer
                    </StyledButton>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Payment */}
              {currentStep === 3 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-gray-800 rounded-xl p-6"
                >
                  <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                    <CreditCard className="h-5 w-5 mr-2" />
                    Méthode de paiement
                  </h2>
                  
                  <div className="space-y-4">
                    <div className={`flex items-center space-x-3 p-4 bg-gray-700 rounded-lg border-2 ${paymentMethod === 'card' ? 'border-iranian-500' : 'border-transparent'}`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="card"
                        checked={paymentMethod === 'card'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="text-iranian-500 focus:ring-iranian-500"
                      />
                      <CreditCard className="h-5 w-5 text-iranian-500" />
                      <span className="text-white">Carte bancaire</span>
                    </div>
                    
                    <div className={`flex items-center space-x-3 p-4 bg-gray-700 rounded-lg border-2 ${paymentMethod === 'paypal' ? 'border-iranian-500' : 'border-transparent'}`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="paypal"
                        checked={paymentMethod === 'paypal'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="text-iranian-500 focus:ring-iranian-500"
                      />
                      <div className="w-5 h-5 bg-blue-500 rounded"></div>
                      <span className="text-white">PayPal</span>
                    </div>
                  </div>
                  
                  {paymentMethod === 'paypal' && (
                    <div className="mt-6">
                      <PaypalButton />
                    </div>
                  )}

                  {paymentMethod === 'card' && (
                    <div className="mt-6">
                      <StripeProvider>
                        <StripeCardForm 
                          onPaymentSuccess={handlePaymentSuccess}
                          onPaymentError={handlePaymentError}
                          shippingInfo={formData}
                        />
                      </StripeProvider>
                    </div>
                  )}

                  <div className="mt-6">
                    <StyledButton
                      onClick={() => setCurrentStep(2)}
                      variant="secondary"
                      size="lg"
                    >
                      Retour
                    </StyledButton>
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-xl p-6 sticky top-6">
              <h2 className="text-xl font-semibold text-white mb-6">
                Résumé de la commande
            </h2>

              {/* Cart Items */}
              <div className="space-y-3 mb-6">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex items-center space-x-3">
                    <img
                      src={item.primary_image || '/placeholder-product.svg'}
                      alt={item.name_fr}
                      className="w-12 h-12 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.src = '/placeholder-product.svg';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white text-sm font-medium truncate">
                        {item.name_fr}
                      </h4>
                      <p className="text-gray-400 text-xs">
                        Quantité: {item.quantity}
                      </p>
                    </div>
                    <span className="text-iranian-400 font-semibold text-sm">
                      {formatPrice((item.sale_price || item.price) * item.quantity)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Summary Details */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-300">
                  <span>Sous-total</span>
                  <span>{formatPrice(getCartTotal())}</span>
                </div>
                <hr className="border-gray-600" />
                <div className="flex justify-between text-white text-lg font-semibold">
                  <span>Total</span>
                  <span>{formatPrice(getCartTotal())}</span>
                </div>
              </div>

              
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage; 